import { Component } from '@theme/component';
import { lockScroll, unlockScroll } from '@theme/utilities';
import { StandardEvents } from '@shopify/events';
import { getScrollTop, getScrollEventTarget, scrollContainerMediaQuery } from '@theme/scroll-container';

/** Where the inline nav replaces the burger; matches the breakpoint in oudh-header.liquid. */
const wideNavQuery = window.matchMedia('(min-width: 1100px)');

/**
 * Header behaviour: publishes its own height for sections that size against it,
 * hides on scroll-down / reveals on scroll-up, and drives the mobile drawer.
 *
 * @extends {Component<{
 *   drawer: HTMLElement,
 *   scrim: HTMLElement,
 *   burger: HTMLButtonElement,
 *   closeButton: HTMLButtonElement,
 *   cartCount: HTMLElement,
 * }>}
 */
class OudhHeader extends Component {
  #lastScrollY = 0;
  #ticking = false;
  /** @type {ResizeObserver | undefined} */
  #resizeObserver;

  connectedCallback() {
    super.connectedCallback();

    this.#measure();
    this.#resizeObserver = new ResizeObserver(() => this.#measure());
    this.#resizeObserver.observe(this);

    this.#lastScrollY = getScrollTop();
    if (this.dataset.sticky === 'true') {
      this.#listenForScroll();
      scrollContainerMediaQuery.addEventListener('change', this.#onBreakpointChange);
    }

    document.addEventListener('keydown', this.#onKeydown);

    document.addEventListener(StandardEvents.cartLinesUpdate, this.#onCartUpdate);

    this.#watchWishlistSlot();

    wideNavQuery.addEventListener('change', this.#onWideNav);

    this.#wireNav();
  }

  disconnectedCallback() {
    document.removeEventListener('pointerdown', this.#onPointerDown, true);
    if (this.#wishlistTimer) clearTimeout(this.#wishlistTimer);
    wideNavQuery.removeEventListener('change', this.#onWideNav);
    unlockScroll(this);
    super.disconnectedCallback?.();
    this.#resizeObserver?.disconnect();
    this.#scrollTarget?.removeEventListener('scroll', this.#onScroll);
    scrollContainerMediaQuery.removeEventListener('change', this.#onBreakpointChange);
    document.removeEventListener('keydown', this.#onKeydown);
    document.removeEventListener(StandardEvents.cartLinesUpdate, this.#onCartUpdate);
  }

  /** @type {ReturnType<typeof setTimeout> | undefined} */
  #wishlistTimer;

  /**
   * Releases the space the CSS holds open for WishlistSuite's header heart when it will not come.
   *
   * The heart arrives late, so the header reserves its cell up front (see `[data-wl-slot]` in
   * oudh-header.liquid). If the embed is off, or it declines to place an icon, that reservation
   * would be a permanent hole. The embed's root div is in the HTML from the start, so its absence
   * is known at once; if it is there but nothing lands within the grace period, give up.
   */
  #watchWishlistSlot = () => {
    const release = () => this.setAttribute('data-wl-reserve', 'off');
    const placed = () => !!this.querySelector('[data-wl-slot]');

    const check = () => {
      if (placed()) return;
      if (!document.querySelector('[id^="wishlist-header-block-"]')) return release();
      this.#wishlistTimer = setTimeout(() => !placed() && release(), 4000);
    };

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', check, { once: true });
    } else {
      check();
    }
  };

  /**
   * Publishes the rendered height for sections that size themselves against the header.
   *
   * `--header-height` and `--header-group-height` are Horizon's own names, kept in sync so stock
   * sections (hero, section.liquid) size themselves correctly alongside this header. The whole
   * header is one sticky element here, so all three values are the same measurement.
   */
  #measure = () => {
    const height = `${this.offsetHeight}px`;

    document.body.style.setProperty('--oudh-header-offset', height);
    document.body.style.setProperty('--header-height', height);
    document.body.style.setProperty('--header-group-height', height);
  };

  /** @type {EventTarget | undefined} */
  #scrollTarget;

  /**
   * Binds the scroll listener to whichever element actually scrolls.
   *
   * At 990px and up Horizon scrolls `.page-wrapper`, not the document, so `window` never
   * emits a scroll event and `window.scrollY` stays at 0. `@theme/scroll-container` resolves
   * the right target for the current breakpoint.
   */
  #listenForScroll = () => {
    this.#scrollTarget = getScrollEventTarget();
    this.#scrollTarget.addEventListener('scroll', this.#onScroll, { passive: true });
  };

  /** The scroll container swaps at 990px, so the listener has to move with it. */
  #onBreakpointChange = () => {
    this.#scrollTarget?.removeEventListener('scroll', this.#onScroll);
    this.#listenForScroll();
    this.#lastScrollY = getScrollTop();
    this.removeAttribute('data-hidden');
  };

  #onScroll = () => {
    if (this.#ticking) return;
    this.#ticking = true;

    requestAnimationFrame(() => {
      const y = getScrollTop();
      const offset = this.offsetHeight;

      // Ignore sub-8px jitter (trackpad inertia, iOS rubber-band) so the bar doesn't flicker.
      if (Math.abs(y - this.#lastScrollY) < 8 && y > offset) {
        this.#ticking = false;
        return;
      }

      // Never hide while the drawer is open, and never within the first header-height of
      // the page, where a hide would read as a flicker.
      if (!this.hasAttribute('data-drawer-open') && y > offset) {
        this.toggleAttribute('data-hidden', y > this.#lastScrollY);
      } else {
        this.removeAttribute('data-hidden');
      }

      this.#lastScrollY = y;
      this.#ticking = false;
    });
  };

  #onKeydown = (/** @type {KeyboardEvent} */ event) => {
    if (event.key === 'Escape') {
      if (this.hasAttribute('data-drawer-open')) return this.closeDrawer();
      this.#dismissMega();
    }

    if (event.key === 'Tab' && this.hasAttribute('data-drawer-open')) this.#trapFocus(event);
  };

  /** Keeps Tab inside the open drawer; the page behind it is not reachable. */
  #trapFocus(/** @type {KeyboardEvent} */ event) {
    const { drawer } = this.refs;
    if (!drawer) return;

    const focusable = /** @type {HTMLElement[]} */ ([
      ...drawer.querySelectorAll('a[href], button:not([disabled]), summary, [tabindex]:not([tabindex="-1"])'),
    ]).filter((el) => el.offsetParent !== null);
    if (!focusable.length) return;

    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    const active = document.activeElement;

    if (!drawer.contains(active)) {
      event.preventDefault();
      first?.focus();
    } else if (event.shiftKey && active === first) {
      event.preventDefault();
      last?.focus();
    } else if (!event.shiftKey && active === last) {
      event.preventDefault();
      first?.focus();
    }
  }

  /** @type {string} */
  #pointerType = '';
  /** @type {Element | null} */
  #touchItem = null;

  /**
   * Mega panels open from CSS (`:hover`, `:focus-within`). This adds what CSS can't:
   * `aria-expanded` kept in step, Escape to dismiss, and a two-tap flow on touch screens wide
   * enough for the inline nav (tablets in landscape), where the first tap used to follow the link
   * before the panel could be used.
   */
  #wireNav() {
    document.addEventListener('pointerdown', this.#onPointerDown, true);

    const nav = this.querySelector('.oudh-header__nav');
    if (!nav) return;

    nav.addEventListener('mouseleave', () => this.removeAttribute('data-mega-off'));
    nav.addEventListener('focusin', () => this.removeAttribute('data-mega-off'));

    for (const item of nav.querySelectorAll('.oudh-header__navitem')) {
      const link = item.querySelector('.oudh-header__link[aria-expanded]');
      if (!link) continue;

      const set = (/** @type {boolean} */ open) => link.setAttribute('aria-expanded', String(open));
      item.addEventListener('mouseenter', () => !this.hasAttribute('data-mega-off') && set(true));
      item.addEventListener('mouseleave', () => set(item.matches(':focus-within')));
      item.addEventListener('focusin', () => set(true));
      item.addEventListener('focusout', () => set(item.matches(':hover')));

      link.addEventListener('click', (event) => {
        // `detail` is 0 for a keyboard-triggered click: Enter on a hybrid laptop that last saw a
        // finger must follow the link, not be swallowed as a "first tap".
        if (this.#pointerType !== 'touch' || event.detail === 0) return;

        // First tap shows the panel (the browser's emulated hover); second tap follows the link.
        if (this.#touchItem !== item) {
          event.preventDefault();
          this.#touchItem = item;
        }
      });
    }
  }

  #onPointerDown = (/** @type {PointerEvent} */ event) => {
    this.#pointerType = event.pointerType;
    const target = /** @type {Element | null} */ (event.target instanceof Element ? event.target : null);
    if (this.#touchItem && !this.#touchItem.contains(target)) this.#touchItem = null;
  };

  /** Escape: drop focus out of the panel and hold it shut until the pointer or focus comes back. */
  #dismissMega() {
    const active = document.activeElement;
    if (!(active instanceof HTMLElement) || !active.closest('.oudh-header__nav')) {
      if (!this.querySelector('.oudh-header__navitem:hover')) return;
    }

    this.setAttribute('data-mega-off', '');
    this.querySelectorAll('.oudh-header__link[aria-expanded]').forEach((link) => {
      link.setAttribute('aria-expanded', 'false');
    });
    if (active instanceof HTMLElement && active.closest('.oudh-header__nav')) active.blur();
  }

  /**
   * Keeps the cart bubble in step with the drawer.
   *
   * The event fires when the request goes out, not when it lands, so the count comes off the
   * event's promise — the same contract Horizon's own cart-icon uses.
   *
   * @param {CustomEvent & { promise?: Promise<{ cart?: { totalQuantity?: number }, detail?: { itemCount?: number } }> }} event
   */
  #onCartUpdate = (event) => {
    const { cartCount } = this.refs;
    if (!cartCount) return;

    event.promise
      ?.then(({ cart, detail }) => {
        cartCount.textContent = String(cart?.totalQuantity ?? detail?.itemCount ?? 0);
      })
      .catch(() => {
        // A stale bubble beats an unhandled rejection; the next page load corrects it.
      });
  };

  /**
   * The burger and drawer disappear at 1100px. Resizing past it with the drawer open (rotating a
   * tablet, resizing a window) would otherwise leave the page scroll-locked behind nothing.
   */
  #onWideNav = (/** @type {MediaQueryListEvent} */ event) => {
    if (event.matches && this.hasAttribute('data-drawer-open')) this.closeDrawer();
  };

  /** @type {((event: TransitionEvent) => void) | undefined} */
  #hideDrawer;

  toggleDrawer() {
    if (this.hasAttribute('data-drawer-open')) {
      this.closeDrawer();
    } else {
      this.openDrawer();
    }
  }

  openDrawer() {
    const { drawer, scrim, burger, closeButton } = this.refs;
    if (!drawer || !scrim) return;

    if (this.#hideDrawer) drawer.removeEventListener('transitionend', this.#hideDrawer);
    this.#hideDrawer = undefined;

    scrim.hidden = false;
    drawer.hidden = false;
    // Force a frame between `hidden = false` and the attribute so the transform animates.
    requestAnimationFrame(() => this.setAttribute('data-drawer-open', ''));

    burger?.setAttribute('aria-expanded', 'true');
    // Horizon's lock: sets `scroll-lock` on <html>, which its stylesheet turns into overflow:hidden
    // on whichever element actually scrolls (the document, or `.page-wrapper` from 990px). Locking
    // `body` alone did nothing from 990px, where the burger still shows.
    lockScroll(this);
    closeButton?.focus();
  }

  closeDrawer() {
    const { drawer, scrim, burger } = this.refs;
    if (!drawer || !scrim) return;

    this.removeAttribute('data-drawer-open');
    burger?.setAttribute('aria-expanded', 'false');
    unlockScroll(this);

    const hide = () => {
      drawer.hidden = true;
      scrim.hidden = true;
    };

    if (this.#hideDrawer) drawer.removeEventListener('transitionend', this.#hideDrawer);

    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      hide();
    } else {
      // Only the drawer's own slide counts. Child transitions (caret, link colour) bubble up here
      // and used to hide the drawer before it had finished leaving.
      this.#hideDrawer = (/** @type {TransitionEvent} */ event) => {
        if (event.target !== drawer || event.propertyName !== 'transform') return;
        drawer.removeEventListener('transitionend', this.#hideDrawer);
        this.#hideDrawer = undefined;
        hide();
      };
      drawer.addEventListener('transitionend', this.#hideDrawer);
    }

    burger?.focus();
  }
}

if (!customElements.get('oudh-header')) {
  customElements.define('oudh-header', OudhHeader);
}

/**
 * Rotating announcement bar.
 *
 * Slides are stacked in a single grid cell, so only opacity changes and the bar never reflows.
 * `visibility` rides along with opacity in CSS, which keeps hidden slides out of the tab order
 * and away from screen readers without needing `display: none` (that would kill the fade).
 *
 * @extends {Component<{ slides: HTMLElement[] }>}
 */
class OudhAnnouncements extends Component {
  #index = 0;
  /** @type {ReturnType<typeof setInterval> | undefined} */
  #timer;

  connectedCallback() {
    super.connectedCallback();

    // Pause while someone is reading or tabbing through a link in the bar.
    this.addEventListener('pointerenter', this.#pause);
    this.addEventListener('pointerleave', this.#play);
    this.addEventListener('focusin', this.#pause);
    this.addEventListener('focusout', this.#play);

    this.#play();
  }

  disconnectedCallback() {
    super.disconnectedCallback?.();
    this.#pause();
  }

  #play = () => {
    const slides = this.refs.slides;
    if (this.dataset.autoplay !== 'true' || !slides || slides.length < 2) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    this.#pause();
    this.#timer = setInterval(() => this.#advance(), Number(this.dataset.interval) || 5000);
  };

  #pause = () => {
    if (this.#timer) clearInterval(this.#timer);
    this.#timer = undefined;
  };

  #advance() {
    const slides = this.refs.slides;
    if (!slides?.length) return;

    slides[this.#index]?.removeAttribute('data-active');
    slides[this.#index]?.setAttribute('aria-hidden', 'true');

    this.#index = (this.#index + 1) % slides.length;

    slides[this.#index]?.setAttribute('data-active', '');
    slides[this.#index]?.removeAttribute('aria-hidden');
  }
}

if (!customElements.get('oudh-announcements')) {
  customElements.define('oudh-announcements', OudhAnnouncements);
}
